import { STATUS_COLOR } from 'src/components/ui/StatusPlate/constants';

export const COMPANY_STATUS_CODE = {
	NOT_IN_WORK: 'not_in_work',
	POSTPONED: 'postponed',
	LPR_CONTACT: 'lpr_contact',
	COMMERCIAL_PROPOSAL: 'commercial_proposal',
	CONTRACT_PREPARATION: 'contract_preparation',
	CONDITIONS_AND_OBJECTIONS: 'conditions_and_objections_negotiation',
	COMPLETED_WITH_VICTORY: 'completed_with_victory',
	COMPLETED_WITH_LOSS: 'completed_with_loss',
};

export const STATUS_HEX = {
	neutral: '#00000029',
	primary: '#0E7DE3',
	success: '#00BB8E',
	error: '#E32323',
};

export const NO_CHANGE_STATUS_OPTION = {
	label: 'Без изменений',
	value: -1,
};

export const STATUS_REASON_OPTIONS = [
	{ label: 'Не удалось связаться', value: 'NO_CONTACT' },
];

export const LOSS_REASON_OPTIONS = STATUS_REASON_OPTIONS;
export const POSTPONED_REASON_OPTIONS = STATUS_REASON_OPTIONS;

const STATUS_REASON_LABELS = {
	NO_CONTACT: 'Не удалось связаться',
	no_contact: 'Не удалось связаться',
};

export const getStatusReasonLabel = (value) => {
	if (value == null || value === '') return null;

	if (typeof value === 'object') {
		const key = value.value ?? value.name ?? value.label;
		return getStatusReasonLabel(key);
	}

	const raw = String(value);
	const fromOptions = STATUS_REASON_OPTIONS.find(
		(option) =>
			String(option.value).toLowerCase() === raw.toLowerCase(),
	);

	return fromOptions?.label ?? STATUS_REASON_LABELS[raw] ?? raw;
};

/** @deprecated use getStatusReasonLabel */
export const getLossReasonLabel = getStatusReasonLabel;

const getLatestTransitionField = (company, field) => {
	const transitions = [...(company?.statusTransitions ?? [])].sort((a, b) => {
		const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
		const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;

		return aTime - bTime;
	});

	for (let i = transitions.length - 1; i >= 0; i -= 1) {
		const value = transitions[i]?.[field];
		if (value) return value;
	}

	return null;
};

export const getCompanyLossReason = (company) => {
	const status = resolveCompanyStatus(company);

	if (status.code !== COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS) {
		return null;
	}

	return getLatestTransitionField(company, 'lossReason');
};

export const getCompanyPostponedReason = (company) => {
	const status = resolveCompanyStatus(company);

	if (status.code !== COMPANY_STATUS_CODE.POSTPONED) {
		return null;
	}

	return getLatestTransitionField(company, 'postponedReason');
};

export const formatCompanyStatusLabel = (company, statuses = []) => {
	const status = resolveCompanyStatus(company, statuses);
	let label = status?.name || 'Не в работе';
	let remark = null;

	if (status?.code === COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS) {
		remark = getStatusReasonLabel(getCompanyLossReason(company));
	}

	if (status?.code === COMPANY_STATUS_CODE.POSTPONED) {
		const postponedUntil =
			company?.cache?.postponedUntil ||
			getLatestTransitionField(company, 'postponedUntil');

		if (postponedUntil) {
			const date = new Date(postponedUntil);
			if (!Number.isNaN(date.getTime())) {
				label = `Отложено до ${date.toLocaleDateString('ru-RU', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
				})}`;
			}
		}

		remark = getStatusReasonLabel(getCompanyPostponedReason(company));
	}

	return { label, remark };
};

const IN_WORK_CODES = new Set([
	COMPANY_STATUS_CODE.LPR_CONTACT,
	COMPANY_STATUS_CODE.COMMERCIAL_PROPOSAL,
	COMPANY_STATUS_CODE.CONTRACT_PREPARATION,
	COMPANY_STATUS_CODE.CONDITIONS_AND_OBJECTIONS,
]);

const REMOVED_STATUS_CODES = new Set(['potential_evaluation', 'in_work']);

export const isCompletedWithLossStatus = (status) => {
	const code = status?.code ?? status?.statusCode;

	if (code) return code === COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS;

	return status?.value === COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS;
};

export const getStatusHexColor = (status) => {
	if (!status) return undefined;

	const code = status.code;

	if (code === COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS) {
		return STATUS_HEX.error;
	}

	if (code === COMPANY_STATUS_CODE.COMPLETED_WITH_VICTORY) {
		return STATUS_HEX.success;
	}

	if (
		code === COMPANY_STATUS_CODE.NOT_IN_WORK ||
		code === COMPANY_STATUS_CODE.POSTPONED ||
		status.group?.code === 'not_in_work'
	) {
		return STATUS_HEX.neutral;
	}

	if (IN_WORK_CODES.has(code) || status.group?.code === 'in_work') {
		return STATUS_HEX.primary;
	}

	if (status.group?.code === 'completed') {
		return STATUS_HEX.success;
	}

	return STATUS_HEX.neutral;
};

export const getStatusPlateColor = (status) => {
	if (!status) return STATUS_COLOR.secondary;

	const code = status.code;

	if (code === COMPANY_STATUS_CODE.COMPLETED_WITH_LOSS) {
		return STATUS_COLOR.error;
	}

	if (code === COMPANY_STATUS_CODE.COMPLETED_WITH_VICTORY) {
		return STATUS_COLOR.success;
	}

	if (
		code === COMPANY_STATUS_CODE.NOT_IN_WORK ||
		code === COMPANY_STATUS_CODE.POSTPONED ||
		status.group?.code === 'not_in_work'
	) {
		return STATUS_COLOR.secondary;
	}

	if (IN_WORK_CODES.has(code) || status.group?.code === 'in_work') {
		return STATUS_COLOR.primary;
	}

	if (status.group?.code === 'completed') {
		return STATUS_COLOR.success;
	}

	return STATUS_COLOR.secondary;
};

export const mapStatusesToSelectOptions = (statuses = []) =>
	statuses
		.filter((status) => status?.code && !REMOVED_STATUS_CODES.has(status.code))
		.map((status) => ({
			label: status.name,
			value: status.id,
			code: status.code,
			color: getStatusHexColor(status),
		}));

export const buildEventStatusSelectProps = (statuses = []) => ({
	label: 'Статус',
	placeholder: 'Без изменений',
	selectOnly: true,
	disableClearable: true,
	defaultValue: NO_CHANGE_STATUS_OPTION,
	options: [NO_CHANGE_STATUS_OPTION, ...mapStatusesToSelectOptions(statuses)],
});

export const findStatusById = (statuses = [], id) =>
	statuses.find((status) => String(status.id) === String(id)) ?? null;

export const findNotInWorkStatus = (statuses = []) =>
	statuses.find(
		(status) => status.code === COMPANY_STATUS_CODE.NOT_IN_WORK,
	) ?? null;

export const resolveCompanyStatus = (company, statuses = []) => {
	if (company?.status) return company.status;

	return (
		findNotInWorkStatus(statuses) ?? {
			name: 'Не в работе',
			code: COMPANY_STATUS_CODE.NOT_IN_WORK,
			group: { code: 'not_in_work' },
		}
	);
};
